import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";

import { getGroupedJackpotByName } from "@common/components/jackpot/helper/groupJackpot";
import JackpotCardBackground from "@common/components/jackpot/card/background";
import MoneyAmount from "@common/components/jackpot/helper/moneyAmount";
import { POPUPS } from "@common/constants/popups";

import './style.scss';

const JackpotCarousel = ({ state, theme, mode, openPopup, closePopup, config, subscribeToJackpotState}) => {
  const data = state?.data || [];
  const trackRef = useRef(null);
  const [, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const [isHovered, setIsHovered] = useState(false);

  const cardCount = getGroupedJackpotByName.length;

  const uniqueNames = useMemo(() => _.uniq(data.map(j => j.jackpotName)), [data]);

  const groupedJackpots = useMemo(() =>
      uniqueNames.map(name => getGroupedJackpotByName(data, name)),
    [data, uniqueNames]);

  const scrollToIndex = useCallback((index, behavior = 'smooth') => {
    const track = trackRef.current;
    if (!track) return;

    const child = track.children[index];
    if (!child) return;

    const left =
      child.offsetLeft - (track.clientWidth - child.clientWidth) / 2;
    const maxLeft = track.scrollWidth - track.clientWidth;

    track.scrollTo({
      left: Math.max(0, Math.min(left, maxLeft)),
      behavior
    });
  }, []);

  const goPrev = useCallback(() => {
    if (cardCount <= 1) return;
    const nextIndex = (activeIndexRef.current - 1 + cardCount) % cardCount;
    activeIndexRef.current = nextIndex;
    setActiveIndex(nextIndex);
    scrollToIndex(nextIndex);
  }, [cardCount, scrollToIndex]);

  const goNext = useCallback(() => {
    if (cardCount <= 1) return;
    const nextIndex = (activeIndexRef.current + 1) % cardCount;
    activeIndexRef.current = nextIndex;
    setActiveIndex(nextIndex);
    scrollToIndex(nextIndex);
  }, [cardCount, scrollToIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const onScroll = () => {
      const trackCenter = track.scrollLeft + track.clientWidth / 2;
      let bestIdx = 0;
      let bestDist = Number.POSITIVE_INFINITY;

      for (let i = 0; i < cardCount; i += 1) {
        const child = track.children[i];
        if (!child) continue;

        const childCenter = child.offsetLeft + child.clientWidth / 2;
        const dist = Math.abs(childCenter - trackCenter);

        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      }

      activeIndexRef.current = bestIdx;
      setActiveIndex(bestIdx);
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      track.removeEventListener('scroll', onScroll);
    };
  }, [cardCount]);

  useEffect(() => {
    if (cardCount <= 0) return;
    activeIndexRef.current = 0;
    setActiveIndex(0);
    scrollToIndex(0, 'auto');
  }, [cardCount, scrollToIndex]);

  useEffect(() => {
    if (cardCount <= 1) return;
    if (isHovered) return;

    const id = window.setInterval(() => {
      goNext();
    }, 3500);

    return () => {
      window.clearInterval(id);
    };
  }, [cardCount, goNext, isHovered]);

  return (
    <div className="jackpot-carousel-container">
      {groupedJackpots.length > 1 && <button
        type="button"
        className="nav-arrow left"
        aria-label="Previous jackpot"
        onClick={goPrev}
        disabled={cardCount <= 1}
      >
        <i className="global-icon icon-slim-arrow-left"/>
      </button>}

      <div
        className="carousel-track"
        ref={trackRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {groupedJackpots.map((jackpot) => (
          <div className="carousel-item" key={jackpot.name}>
            <div
              className="jackpot-card"
              onClick={() => openPopup(POPUPS.JACKPOT_LIST_POPUP, {
                jackpotName: jackpot.name,
                state,
                subscribeToJackpotState,
                closePopup,
                mode,
                theme,
                config
              })}
            >
              <JackpotCardBackground theme={theme} mode={mode} />
              <div className="jackpot-card-content">
                <h3>{jackpot.name}</h3>
                <h1>
                  <MoneyAmount config={config} currencyConfig={{ name: jackpot.currencyId }} amount={jackpot.totalAmount} round={jackpot.spinningRounding} />
                </h1>
              </div>
            </div>
          </div>
        ))}
      </div>

      {groupedJackpots.length > 1 && <button
        type="button"
        className="nav-arrow right"
        aria-label="Next jackpot"
        onClick={goNext}
        disabled={cardCount <= 1}
      >
        <i className="global-icon icon-slim-arrow-right"/>
      </button>}
    </div>
  );
};

export default JackpotCarousel;
